export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

export interface KdfParameters {
  algorithm: "argon2id";
  version: 1;
  salt: string;
  memorySize: number;
  iterations: number;
  parallelism: number;
  hashLength: 32;
}

export interface WrappedEnvelope {
  version: 1;
  algorithm: "A256GCM";
  iv: string;
  ciphertext: string;
}

export interface DescriptorEvent {
  version: 1;
  rootDid: string;
  sequence: number;
  previousEventHash: string | null;
  nodeEndpoint: string;
  operationalKeys: string[];
  issuedAt: string;
  supersedesEndpoint: string | null;
}

export interface SignedDescriptorEvent extends DescriptorEvent {
  signature: {
    algorithm: "ES256";
    encoding: "p1363";
    value: string;
  };
}

export interface AccessTokenResponse {
  accessToken: string;
  tokenType: "Bearer";
  expiresIn: number;
  refreshToken?: string;
  refreshExpiresIn?: number;
  scopes: string[];
  handle: string;
  rootDid: string;
}

export const CLIENT_KDF_DEFAULTS = {
  memorySize: 32 * 1024,
  iterations: 2,
  parallelism: 1,
  hashLength: 32 as const,
};

export const RECOVERY_KDF_DEFAULTS = {
  memorySize: 32 * 1024,
  iterations: 2,
  parallelism: 1,
  hashLength: 32 as const,
};

export const textEncoder = new TextEncoder();
export const textDecoder = new TextDecoder();

export function bytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return new Uint8Array(bytes).buffer;
}

export function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

export function fromBase64Url(value: string): Uint8Array {
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function randomBase64Url(length = 32): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return toBase64Url(bytes);
}

export async function sha256Base64Url(value: string | Uint8Array): Promise<string> {
  const bytes = typeof value === "string" ? textEncoder.encode(value) : value;
  return toBase64Url(new Uint8Array(await crypto.subtle.digest("SHA-256", bytesToArrayBuffer(bytes))));
}

export function canonicalJson(value: JsonValue): string {
  return JSON.stringify(canonicalize(value));
}

function canonicalize(value: JsonValue): JsonValue {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) {
      throw new TypeError("Canonical JSON accepts safe integers only.");
    }
    return value;
  }
  if (typeof value === "string" || typeof value === "boolean" || value === null) {
    return value;
  }
  if (Array.isArray(value)) return value.map(canonicalize);

  const sorted: { [key: string]: JsonValue } = {};
  for (const key of Object.keys(value).sort()) {
    const child = value[key];
    if (child === undefined) throw new TypeError("Canonical JSON rejects undefined.");
    sorted[key] = canonicalize(child);
  }
  return sorted;
}

export function descriptorPayload(descriptor: SignedDescriptorEvent | DescriptorEvent): DescriptorEvent {
  return {
    version: descriptor.version,
    rootDid: descriptor.rootDid,
    sequence: descriptor.sequence,
    previousEventHash: descriptor.previousEventHash,
    nodeEndpoint: descriptor.nodeEndpoint,
    operationalKeys: descriptor.operationalKeys,
    issuedAt: descriptor.issuedAt,
    supersedesEndpoint: descriptor.supersedesEndpoint,
  };
}

export function didFromPublicJwk(jwk: JsonWebKey): string {
  if (jwk.kty !== "EC" || jwk.crv !== "P-256" || !jwk.x || !jwk.y) {
    throw new TypeError("Expected a P-256 public JWK.");
  }
  const x = fromBase64Url(jwk.x);
  const y = fromBase64Url(jwk.y);
  if (x.length !== 32 || y.length !== 32) throw new TypeError("Invalid P-256 coordinates.");
  const compressed = new Uint8Array(33);
  compressed[0] = (y[31]! & 1) === 0 ? 0x02 : 0x03;
  compressed.set(x, 1);
  return `did:vibe:p256:${toBase64Url(compressed)}`;
}

export function normalizeAppOrigin(input: string): string {
  const url = new URL(input);
  const isLoopback = url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "[::1]";
  if (url.protocol !== "https:" && !(url.protocol === "http:" && isLoopback)) {
    throw new TypeError("App origins must use HTTPS; loopback HTTP is the only exception.");
  }
  if (url.username || url.password || url.pathname !== "/" || url.search || url.hash) {
    throw new TypeError("Expected an origin without credentials, path, query, or fragment.");
  }
  return url.origin;
}

export function validateRedirectUri(clientId: string, redirectUri: string): URL {
  const clientOrigin = normalizeAppOrigin(clientId);
  const redirect = new URL(redirectUri);
  if (redirect.origin !== clientOrigin) throw new TypeError("redirect_uri must match client_id origin.");
  return redirect;
}

export function normalizeHandle(value: string): string {
  const handle = value.trim().toLowerCase();
  if (!/^[a-z][a-z0-9-]{2,30}$/u.test(handle) || handle.endsWith("-")) {
    throw new TypeError("Use 3–31 lowercase letters, numbers, or hyphens; start with a letter.");
  }
  return handle;
}

export function normalizeDisplayName(value: string): string {
  const name = value.normalize("NFC").trim().replace(/\s+/gu, " ");
  if (name.length < 1 || name.length > 80) throw new TypeError("Display name must be 1–80 characters.");
  return name;
}

export function normalizeEmail(value: string): string {
  const email = value.normalize("NFKC").trim().toLowerCase();
  const separator = email.lastIndexOf("@");
  const local = email.slice(0, separator);
  const domain = email.slice(separator + 1);
  if (
    email.length > 254 ||
    separator <= 0 ||
    local.length > 64 ||
    domain.length < 3 ||
    !domain.includes(".") ||
    /\s/u.test(email) ||
    local.includes("@") ||
    domain.startsWith(".") ||
    domain.endsWith(".")
  ) {
    throw new TypeError("Enter a valid email address.");
  }
  return email;
}
